import type { ComponentPropsWithoutRef, Ref } from 'react'
import { cn } from '#utils'

/**
 * Button component
 * 
 * Native <button> element with consistent styling and accessibility.
 * Supports multiple variants and sizes with proper focus states.
 * 
 * @example
 * ```tsx
 * <Button variant="primary" onClick={handleClick}>Save</Button>
 * <Button variant="outline" size="sm" disabled>Cancel</Button>
 * ```
 * 
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/button/
 */
export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  /**
   * Visual style variant
   *
   * `default` and `outline` paint with Butter's own brand. **`theme` and `themeOutline` paint
   * with the CLUB's** — the `--theme-*` custom properties a tenant sets — and are what any
   * member-facing surface wants. Reach for them on pages a member sees as their club rather
   * than as Butter; using `default` there silently ships Butter purple to every tenant.
   *
   * @default 'default'
   */
  variant?: 'default' | 'theme' | 'themeOutline' | 'outline' | 'secondary' | 'ghost' | 'ghostDestructive' | 'destructive' | 'link'
  /**
   * Button size
   * @default 'default'
   */
  size?: 'default' | 'sm' | 'lg' | 'xl' | 'icon'
  /**
   * Whether the button is in a loading state
   */
  isLoading?: boolean
}

export function Button({
  variant = 'default',
  size = 'default',
  isLoading,
  className,
  disabled,
  children,
  type = 'button',
  ref,
  ...props
}: ButtonProps & { ref?: Ref<HTMLButtonElement> }) {
  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      data-size={size}
      data-loading={isLoading}
      disabled={disabled || isLoading}
      className={cn(
        // Base styles
        'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium',
        'transition-colors',
        // Focus styles (always visible)
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butter-brand-600 focus-visible:ring-offset-2',
        // …but a club-themed button rings in the club's colour, not Butter's. Without this a
        // tenant-branded CTA flashes Butter purple on keyboard focus.
        'data-[variant=theme]:focus-visible:ring-theme-600 data-[variant=themeOutline]:focus-visible:ring-theme-600',
        // Disabled state
        'disabled:pointer-events-none disabled:opacity-50',
        // SVG child sizing
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        
        // Variant styles using data attributes
        'data-[variant=default]:bg-butter-brand-600 data-[variant=default]:text-white data-[variant=default]:hover:bg-butter-brand-700',
        // Club-themed variants — `--theme-*`, set per tenant. Deliberately byte-for-byte
        // equivalent to the `.btn-primary` / `.btn-outline` classes in `styles/components.css`
        // (same palette, radius, sizes and focus ring), so a surface can move from those
        // classes to this component without a visual diff. The classes only work in apps that
        // import the full stylesheet, which web-portal-2 does not; this component works
        // anywhere the preset is loaded.
        'data-[variant=theme]:bg-theme-600 data-[variant=theme]:text-white data-[variant=theme]:text-interactive:bg-theme-700',
        // Matches `.btn-primary:disabled` — flat neutral, not a faded club colour.
        'data-[variant=theme]:disabled:bg-butter-gray-100 data-[variant=theme]:disabled:text-butter-gray-300 data-[variant=theme]:disabled:opacity-100',
        'data-[variant=themeOutline]:border data-[variant=themeOutline]:border-theme-600 data-[variant=themeOutline]:bg-white data-[variant=themeOutline]:text-interactive:bg-theme-50',
        'data-[variant=destructive]:bg-red-600 data-[variant=destructive]:text-white data-[variant=destructive]:hover:bg-red-700',
        'data-[variant=outline]:border data-[variant=outline]:border-butter-brand-600 data-[variant=outline]:bg-white data-[variant=outline]:hover:bg-butter-brand-50 data-[variant=outline]:hover:text-gray-900',
        'data-[variant=secondary]:bg-gray-100 data-[variant=secondary]:text-gray-900 data-[variant=secondary]:hover:bg-gray-200',
        'data-[variant=ghost]:hover:bg-gray-100 data-[variant=ghost]:hover:text-gray-900',
        // Ghost destructive — transparent bg, red text darkening on hover (for text-style delete/void actions)
        'data-[variant=ghostDestructive]:bg-transparent data-[variant=ghostDestructive]:text-red-600 data-[variant=ghostDestructive]:hover:text-red-700',
        'data-[variant=link]:text-butter-brand-600 data-[variant=link]:underline-offset-4 data-[variant=link]:hover:underline',
        
        // Size styles using data attributes
        'data-[size=default]:h-10 data-[size=default]:px-4 data-[size=default]:py-2',
        'data-[size=sm]:h-9 data-[size=sm]:px-3',
        'data-[size=lg]:h-11 data-[size=lg]:px-8',
        'data-[size=xl]:h-12 data-[size=xl]:px-8 data-[size=xl]:text-base data-[size=xl]:font-semibold',
        'data-[size=icon]:h-10 data-[size=icon]:w-10',
        
        // User overrides
        className
      )}
      {...props}
    >
      {isLoading ? 'Loading...' : children}
    </button>
  )
}
